'use client'
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { _intersection, tranformToMap } from '@src/utils/general.utils'

type TQueryState<T extends string> = Record<T, string | undefined>

export function useAllQueryState<T extends string>(
  queryKeys: T[],
  fallback?: TQueryState<T>,
  shouldReplace?: boolean,
): [TQueryState<T>, (newQueryState: TQueryState<T>, navigateToOverwrite: string | null) => string] {
  const searchParams = useSearchParams()
  const router = useRouter()

  const parseAllWatchedQuery = useCallback<() => TQueryState<T>>(() => {
    const searchParamKeys = Array.from(searchParams.keys())
    const queriedSearchParamKeys = _intersection(searchParamKeys, queryKeys)

    if (queriedSearchParamKeys.length === 0) {
      return fallback ?? ({} as TQueryState<T>)
    }

    const allQueryStates = {} as TQueryState<T>
    queryKeys.forEach(queryKey => {
      const queryValues = searchParams.get(queryKey)
      if (queryValues) allQueryStates[queryKey] = queryValues
    })
    return allQueryStates
  }, [searchParams, fallback, queryKeys])

  const [allWatchedQuery, setAllWatchedQuery] = useState<TQueryState<T>>(parseAllWatchedQuery)

  useEffect(() => {
    setAllWatchedQuery(parseAllWatchedQuery)
  }, [parseAllWatchedQuery])

  const getUnwatchedQueryString = () => {
    const unwatchedMap = {} as Record<string, string>
    for (const key of searchParams.keys()) {
      if (!(queryKeys as string[]).includes(key)) {
        unwatchedMap[key] = searchParams.get(key) ?? ''
      }
    }
    return new URLSearchParams(unwatchedMap).toString()
  }

  const createTargetURL = (updatedWatchedQueryState: TQueryState<T>) => {
    const unwatchedQueryString = getUnwatchedQueryString()
    const watchedQueryString = new URLSearchParams(tranformToMap(updatedWatchedQueryState)).toString()

    if (!unwatchedQueryString && !watchedQueryString) return ''
    if (unwatchedQueryString && watchedQueryString) return `?${unwatchedQueryString}&${watchedQueryString}`
    return `?${unwatchedQueryString || watchedQueryString}`
  }

  const setQueryStateHandler = (updatedWatchedQueryState: TQueryState<T>, navigateToOverwrite: string | null) => {
    // TODO: read only watched queries
    setAllWatchedQuery(updatedWatchedQueryState)
    const targetURL = navigateToOverwrite ?? createTargetURL(updatedWatchedQueryState)
    const pathname = window.location.pathname
    if (shouldReplace) {
      router.replace(pathname + targetURL)
    } else {
      router.push(pathname + targetURL)
    }
    return targetURL
  }

  return [allWatchedQuery, setQueryStateHandler]
}
